import api from "@/config/api";
import type { RequestOptions } from "@/shared/types/api.types";
import { API_ENDPOINTS } from "@/constants/api-endpoints";
import type { PageResponseDTO } from "@/shared/types/pagination.types";
import type {
  InvoiceResponseDTO,
  InvoiceSearchParams,
  InvoiceUpdateRequestDTO,
  ManualInvoiceRequestDTO,
  ResendInvoiceRequestDTO,
} from "../types/invoice.types";

const RESEND_TIMEOUT_MS = 60_000;

export const invoiceService = {
  search: async (
    params?: InvoiceSearchParams,
    options?: RequestOptions,
  ): Promise<PageResponseDTO<InvoiceResponseDTO>> => {
    const response = await api.get<PageResponseDTO<InvoiceResponseDTO>>(
      API_ENDPOINTS.INVOICES_ADMIN.SEARCH,
      { params, signal: options?.signal },
    );
    return response.data;
  },

  /**
   * Emissão manual de NF avulsa (ADMIN). A resposta é síncrona e reflete o
   * resultado imediato da chamada à Focus NFe (`WAITING` ou `ERROR`). A
   * autorização final (`ISSUED`) chega depois via webhook — reconsultar via
   * `getById` para acompanhar.
   */
  emit: async (data: ManualInvoiceRequestDTO): Promise<InvoiceResponseDTO> => {
    const response = await api.post<InvoiceResponseDTO>(
      API_ENDPOINTS.INVOICES_ADMIN.EMIT,
      data,
    );
    return response.data;
  },

  getById: async (
    id: number,
    options?: RequestOptions,
  ): Promise<InvoiceResponseDTO> => {
    const response = await api.get<InvoiceResponseDTO>(
      API_ENDPOINTS.INVOICES_ADMIN.BY_ID(id),
      { signal: options?.signal },
    );
    return response.data;
  },

  update: async (
    id: number,
    data: InvoiceUpdateRequestDTO,
  ): Promise<InvoiceResponseDTO> => {
    const response = await api.put<InvoiceResponseDTO>(
      API_ENDPOINTS.INVOICES_ADMIN.BY_ID(id),
      data,
    );
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await api.delete(API_ENDPOINTS.INVOICES_ADMIN.BY_ID(id));
  },

  cancel: async (id: number): Promise<void> => {
    await api.post(API_ENDPOINTS.INVOICES_ADMIN.CANCEL(id));
  },

  resend: async (
    id: number,
    data?: ResendInvoiceRequestDTO,
  ): Promise<InvoiceResponseDTO> => {
    const response = await api.post<InvoiceResponseDTO>(
      API_ENDPOINTS.INVOICES_ADMIN.RESEND(id),
      data,
      { timeout: RESEND_TIMEOUT_MS },
    );
    return response.data;
  },

  downloadPdf: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(API_ENDPOINTS.INVOICES_ADMIN.PDF(id), {
      responseType: "blob",
    });
    return response.data;
  },

  downloadCanceledPdf: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(
      API_ENDPOINTS.INVOICES_ADMIN.PDF_CANCELED(id),
      { responseType: "blob" },
    );
    return response.data;
  },

  downloadXml: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(API_ENDPOINTS.INVOICES_ADMIN.XML(id), {
      responseType: "blob",
    });
    return response.data;
  },

  downloadCanceledXml: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(
      API_ENDPOINTS.INVOICES_ADMIN.XML_CANCELED(id),
      { responseType: "blob" },
    );
    return response.data;
  },
};
